/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2024, Zumper
 */

import { ForwardedRef, type ReactNode, forwardRef } from 'react'
import type { Link, LinkProps } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

type BaseButtonProps = {
  children?: ReactNode | undefined
  isLoading?: boolean
  size?: (typeof sizes)[number]
  variant?: (typeof variants)[number]
}

type ButtonPropsButton = JSX.IntrinsicElements['button'] &
  BaseButtonProps & { as?: 'button' }
type ButtonPropsA = JSX.IntrinsicElements['a'] & BaseButtonProps & { as: 'a' }
type ButtonPropsLink<LocationState = unknown> = LinkProps<LocationState> &
  BaseButtonProps & { as: typeof Link<LocationState> }

export type ButtonProps<
  As extends undefined | 'a' | 'button' | typeof Link =
    | undefined
    | 'a'
    | 'button'
    | typeof Link,
> =
  | ('a' extends As ? ButtonPropsA : never)
  | ('button' extends As ? ButtonPropsButton : never)
  | (typeof Link extends As ? ButtonPropsLink : never)
  | (undefined extends As ? ButtonPropsButton : never)

const sizes = ['xs', 'sm', 'md', 'lg'] as const

const variants = [
  'solid',
  'pink',
  'secondary',
  'ghost',
  'outline',
  'link',
] as const

interface _Button {
  (props: ButtonProps<'a'>): JSX.Element
  (props: ButtonProps<typeof Link>): JSX.Element
  (props: ButtonProps<'button' | undefined>): JSX.Element
}

export const Button = Object.assign(
  forwardRef(
    (
      {
        as: Component = 'button',
        children,
        className,
        isLoading,
        size = 'sm',
        variant = 'solid',
        ...props
      }: ButtonProps,
      ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement | Link<unknown>>
    ) => {
      return (
        // @ts-expect-error no property children on IntristicAttributes but all Components get it from BaseButtonProps
        <Component
          {...(Component === 'button' ? { type: 'button' } : undefined)}
          {...props}
          {...(Component === 'button'
            ? // @ts-expect-error props.disabled doesn't exist if Component is 'a'
              { disabled: isLoading || props.disabled }
            : undefined)}
          ref={ref}
          className={twMerge(
            'appearance-none',
            'border',
            'border-solid',
            'cursor-pointer',
            'disabled:cursor-not-allowed',
            'disabled:opacity-40',
            'disabled:shadow-none',
            'duration-200',
            'font-body',
            'font-bold',
            'inline-flex',
            'items-center',
            'justify-center',
            'leading-[0.9]',
            'relative',
            'rounded-md',
            'select-none',
            'transition',
            'whitespace-nowrap',

            size === 'xs' && 'h-8 px-3 text-sm',
            size === 'sm' && 'h-9 px-4 text-base',
            size === 'md' && 'h-10 px-4 text-base',
            size === 'lg' && 'h-12 px-6 text-base',

            variant === 'solid' &&
              'bg-blue-900 border-0 text-gray-0 hover:bg-blue-800 hover:disabled:bg-blue-900 active:bg-blue-800 active:disabled:bg-blue-900',
            variant === 'pink' &&
              'bg-pink-600 border-0 text-gray-0 hover:bg-pink-650 hover:disabled:bg-pink-600',
            variant === 'secondary' &&
              'bg-gray-150 border-0 text-gray-950 hover:bg-gray-250 hover:disabled:bg-gray-150',
            variant === 'ghost' &&
              'bg-transparent border-0 text-gray-950 hover:bg-gray-50 hover:disabled:bg-transparent',
            variant === 'outline' &&
              'bg-gray-0 text-gray-950 border-gray-350 hover:bg-gray-50 hover:disabled:bg-gray-0 hover:border-gray-800 hover:disabled:border-gray-350',
            variant === 'link' &&
              'bg-transparent border-0 p-0 text-gray-900 hover:underline hover:disabled:no-underline',

            className
          )}
        >
          {isLoading ? (
            <>
              <div
                aria-label="Loading…"
                className="absolute animate-spin [animation-duration:450ms] border-2 border-solid border-t-current border-b-transparent border-l-transparent border-r-current box-content rounded-full size-2.5 inset-0 m-auto"
              />
              <span className="opacity-0">{children}</span>
            </>
          ) : (
            children
          )}
        </Component>
      )
    }
  ) as _Button,
  { sizes, variants }
)
